import {
  Alert,
  Backdrop,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Icon,
  Snackbar,
  Tooltip,
  Grid,
  Divider,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";
import usePageTitle from "app/pageTitle";
import MDInput from "theme/components/MDInput";
import MDButton from "theme/components/MDButton";
import DataTable from "theme/Tables/DataTable";
import axios from "axios.js";

function PredictPromotionPage() {
  usePageTitle("Predict & Win - Promotion");

  const navigate = useNavigate();
  const { ref } = useParams();

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("error");
  const [refreshPage, setRefreshPage] = useState(false);
  const [pageLoaded, setPageLoaded] = useState(false);
  const [promotion, setPromotion] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openCreateQuestionDialog, setOpenCreateQuestionDialog] = useState(false);

  const [newQuestion, setNewQuestion] = useState("");
  const [newQuestionType, setNewQuestionType] = useState(null);
  const [newAnswerType, setNewAnswerType] = useState(null);
  const [newAnswerValueType, setNewAnswerValueType] = useState(null);
  const [newSelectionCardinality, setNewSelectionCardinality] = useState(null);
  const [newQuestionOpensAt, setNewQuestionOpensAt] = useState("");
  const [newQuestionClosesAt, setNewQuestionClosesAt] = useState("");
  const [newQuestionPosition, setNewQuestionPosition] = useState("0");

  const [newQuestionOptions, setNewQuestionOptions] = useState(["", ""]);

  const [newControlledAnswers, setNewControlledAnswers] = useState([
    { label: "", value: "" },
    { label: "", value: "" },
  ]);

  const [promotionTitle, setPromotionTitle] = useState("");
  const [promotionDescription, setPromotionDescription] = useState("");
  const [promotionOpensAt, setPromotionOpensAt] = useState("");
  const [promotionClosesAt, setPromotionClosesAt] = useState("");

  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const showMessage = (message, messageSeverity = "error") => {
    setSeverity(messageSeverity);
    setErrMsg(message || "An unexpected error occurred.");
    setOpen(true);
  };

  const handleRequestError = () => {
    setShowLoading(false);
    showMessage(
      `Unable to complete request. ${
        navigator.onLine ? "" : "Make sure you have an active internet connection."
      }`
    );
  };

  const toDateTimeInput = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return localDate.toISOString().slice(0, 16);
  };

  const formatDate = (value) => {
    if (!value) return "---";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
  };

  const showStatus = (status) => (
    <MDTypography variant="body2" color={status ? "success" : "text"}>
      {status ? "Active" : "Paused"}
    </MDTypography>
  );

  const questionColumns = [
    { Header: "Question", accessor: "question", align: "left" },
    // { Header: "Position", accessor: "position", align: "center" },
    { Header: "Opens", accessor: "open", align: "left" },
    { Header: "Closes", accessor: "close", align: "cenleftter" },
    { Header: "Status", accessor: "status", align: "center" },
    // { Header: "Created", accessor: "created", align: "left" },
    { Header: "Actions", accessor: "actions", align: "right" },
  ];

  const buildQuestionRows = (data) =>
    data.map((row) => ({
      question: row.question || "---",
      // position: row.position ?? "---",
      status: showStatus(row.status),
      open: row.opens_at,
      close: row.closes_at,
      created: row.created,
      actions: (
        <MDBox display="flex" justifyContent="flex-end" gap={1}>
          <Tooltip title="Manage Question">
            <MDButton
              size="small"
              variant="outlined"
              color="dark"
              iconOnly
              onClick={() => navigate(`/tm/predict/promotions/questions/${row.reference}`)}
            >
              <Icon fontSize="small">settings</Icon>
            </MDButton>
          </Tooltip>
          <Tooltip title={row.status ? "Pause Question" : "Activate Question"}>
            <MDButton
              size="small"
              variant="outlined"
              color={row.status ? "warning" : "success"}
              iconOnly
              onClick={() => updateQuestionStatus(row)}
            >
              <Icon fontSize="small">{row.status ? "pause" : "play_arrow"}</Icon>
            </MDButton>
          </Tooltip>
        </MDBox>
      ),
    }));

  const fetchPromotion = async () => {
    setShowLoading(true);

    try {
      const result = await axios.get(`/portal/tm/predict/promotions/${ref}/fetch.json`);

      if (result.data.status === "success") {
        const responseData = result.data.data;
        const data = responseData.promotion || responseData;

        setPromotion(data);
        setQuestions(buildQuestionRows(responseData.questions || data.questions || []));
        setPromotionTitle(data.title || "");
        setPromotionDescription(data.description || "");
        setPromotionOpensAt(toDateTimeInput(data.opens_at));
        setPromotionClosesAt(toDateTimeInput(data.closes_at));
        setPageLoaded(true);
      } else {
        showMessage(result.data.data.message);
      }
    } catch (error) {
      handleRequestError();
      return;
    }

    setShowLoading(false);
  };

  useEffect(() => {
    if (ref) fetchPromotion();
  }, [ref, refreshPage]);

  const editPromotion = async () => {
    if (!promotionTitle.trim()) {
      showMessage("Enter promotion title.");
      return;
    }

    if (!promotionOpensAt || !promotionClosesAt) {
      showMessage("Select the promotion opening and closing dates.");
      return;
    }

    if (new Date(promotionClosesAt) <= new Date(promotionOpensAt)) {
      showMessage("The closing date must be after the opening date.");
      return;
    }

    setOpenEditDialog(false);
    setShowLoading(true);

    try {
      const result = await axios.post("/portal/tm/predict/promotions/edit.json", {
        reference: ref,
        title: promotionTitle.trim(),
        description: promotionDescription,
        opens_at: new Date(promotionOpensAt).toISOString(),
        closes_at: new Date(promotionClosesAt).toISOString(),
      });

      if (result.data.status === "success") {
        showMessage(result.data.data.message || "Promotion updated successfully.", "success");
        setRefreshPage((previous) => !previous);
      } else {
        showMessage(result.data.data.message);
      }
    } catch (error) {
      handleRequestError();
      return;
    }

    setShowLoading(false);
  };

  const resetQuestionForm = () => {
    setNewQuestion("");
    setNewQuestionType(null);
    setNewAnswerType(null);
    setNewAnswerValueType(null);
    setNewSelectionCardinality(null);
    setNewQuestionOpensAt(promotion?.opens_at ? toDateTimeInput(promotion.opens_at) : "");
    setNewQuestionClosesAt(promotion?.closes_at ? toDateTimeInput(promotion.closes_at) : "");
    setNewQuestionPosition(String(questions.length));
    setNewQuestionOptions(["", ""]);
    setNewControlledAnswers([
      { label: "", value: "" },
      { label: "", value: "" },
    ]);
  };

  const addQuestionOption = () => {
    setNewQuestionOptions((previous) => [...previous, ""]);
  };

  const removeQuestionOption = (index) => {
    setNewQuestionOptions((previous) => previous.filter((_, optionIndex) => optionIndex !== index));
  };

  const updateQuestionOption = (index, value) => {
    setNewQuestionOptions((previous) =>
      previous.map((option, optionIndex) => (optionIndex === index ? value : option))
    );
  };

  const addControlledAnswer = () => {
    setNewControlledAnswers((previous) => [...previous, { label: "", value: "" }]);
  };

  const removeControlledAnswer = (index) => {
    setNewControlledAnswers((previous) =>
      previous.filter((_, answerIndex) => answerIndex !== index)
    );
  };

  const updateControlledAnswer = (index, field, value) => {
    setNewControlledAnswers((previous) =>
      previous.map((answer, answerIndex) =>
        answerIndex === index ? { ...answer, [field]: value } : answer
      )
    );
  };

  const createQuestion = async () => {
    if (!newQuestion.trim()) {
      showMessage("Enter the question.");
      return;
    }

    if (!newQuestionOpensAt || !newQuestionClosesAt) {
      showMessage("Select the question opening and closing dates.");
      return;
    }

    if (new Date(newQuestionClosesAt) <= new Date(newQuestionOpensAt)) {
      showMessage("The closing date must be after the opening date.");
      return;
    }

    if (!Number.isInteger(Number(newQuestionPosition)) || Number(newQuestionPosition) < 0) {
      showMessage("Position must be a non-negative whole number.");
      return;
    }

    let questionOptions = [];
    let controlledAnswers = [];

    if (newQuestionType === "OPTIONS_ANSWERS") {
      questionOptions = newQuestionOptions.map((title) => title.trim()).filter(Boolean);

      if (questionOptions.length < 2) {
        showMessage("Add at least two question options.");
        return;
      }

      if (
        new Set(questionOptions.map((title) => title.toLowerCase())).size !== questionOptions.length
      ) {
        showMessage("Question options must not contain duplicates.");
        return;
      }
    }

    if (newAnswerType === "CONTROLLED") {
      controlledAnswers = newControlledAnswers.map((answer) => ({
        label: answer.label.trim(),
        value: answer.value.trim(),
      }));

      if (controlledAnswers.some((answer) => !answer.label || !answer.value)) {
        showMessage("Enter both a label and a value for every controlled answer.");
        return;
      }

      if (controlledAnswers.length < 2) {
        showMessage("Add at least two controlled answer choices.");
        return;
      }

      const answerValues = controlledAnswers.map((answer) => answer.value.toLowerCase());

      if (new Set(answerValues).size !== answerValues.length) {
        showMessage("Controlled answer values must be unique.");
        return;
      }
    }

    setOpenCreateQuestionDialog(false);
    setShowLoading(true);

    try {
      const result = await axios.post("/portal/tm/predict/promotions/questions/create.json", {
        promotion_reference: ref,
        question: newQuestion.trim(),
        question_type: newQuestionType,
        answer_type: newAnswerType,
        answer_value_type: newAnswerType === "OPEN" ? newAnswerValueType : null,
        selection_cardinality: newAnswerType === "CONTROLLED" ? newSelectionCardinality : null,
        opens_at: new Date(newQuestionOpensAt).toISOString(),
        closes_at: new Date(newQuestionClosesAt).toISOString(),
        position: Number(newQuestionPosition),
        options:
          newQuestionType === "OPTIONS_ANSWERS"
            ? questionOptions.map((title, index) => ({
                title,
                position: index,
              }))
            : [],
        answers:
          newAnswerType === "CONTROLLED"
            ? controlledAnswers.map((answer, index) => ({
                label: answer.label,
                value: answer.value,
                position: index,
              }))
            : [],
      });

      if (result.data.status === "success") {
        showMessage(result.data.data.message || "Question created successfully.", "success");
        resetQuestionForm();
        setRefreshPage((previous) => !previous);
      } else {
        showMessage(result.data.data.message);
      }
    } catch (error) {
      handleRequestError();
      return;
    }

    setShowLoading(false);
  };

  const updateQuestionStatus = async (question) => {
    setShowLoading(true);

    try {
      const result = await axios.get(
        `/portal/tm/predict/promotions/questions/${question.reference}/status.json`
      );

      if (result.data.status === "success") {
        showMessage(result.data.data.message || "Question status updated successfully.", "success");
        setRefreshPage((previous) => !previous);
      } else {
        showMessage(result.data.data.message);
      }
    } catch (error) {
      handleRequestError();
      return;
    }

    setShowLoading(false);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox py={3}>
        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <MDBox>
            <MDTypography variant="h4" fontWeight="medium">
              {promotion?.title || "Promotion"}
            </MDTypography>
            <MDTypography variant="button" color="text">
              Manage promotion details and questions.
            </MDTypography>
          </MDBox>

          <MDBox display="flex" gap={1}>
            <MDButton
              variant="outlined"
              color="dark"
              onClick={() => navigate("/tm/predict/promotions")}
              startIcon={<Icon>west</Icon>}
              size="small"
            >
              Back
            </MDButton>
            <MDButton
              variant="contained"
              color="dark"
              startIcon={<Icon>edit</Icon>}
              onClick={() => setOpenEditDialog(true)}
              disabled={!promotion}
              size="small"
            >
              Edit Promotion
            </MDButton>
          </MDBox>
        </MDBox>

        {promotion && (
          <Card sx={{ mb: 3 }}>
            <MDBox p={3}>
              <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
                <MDBox>
                  <MDTypography variant="h6">{promotion.title}</MDTypography>
                  <MDTypography variant="button" color="text">
                    {promotion.description || "No description provided."}
                  </MDTypography>
                </MDBox>
                {showStatus(promotion.status)}
              </MDBox>

              <MDBox mt={2} display="flex" flexWrap="wrap" gap={4}>
                <MDBox>
                  <MDTypography variant="body2" color="text">
                    Opens
                  </MDTypography>
                  <MDTypography variant="button">{formatDate(promotion.opens_at)}</MDTypography>
                </MDBox>
                <MDBox>
                  <MDTypography variant="body2" color="text">
                    Closes
                  </MDTypography>
                  <MDTypography variant="button">{formatDate(promotion.closes_at)}</MDTypography>
                </MDBox>
              </MDBox>
            </MDBox>
          </Card>
        )}

        <Card>
          <MDBox p={0}>
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              gap={2}
              mb={2}
              p={2}
            >
              <MDBox>
                <MDTypography variant="h6">Questions</MDTypography>
                <MDTypography variant="button" color="text">
                  Open a question to manage its prizes, predictions, and outcome.
                </MDTypography>
              </MDBox>

              <MDButton
                variant="contained"
                color="dark"
                startIcon={<Icon>add</Icon>}
                onClick={() => {
                  resetQuestionForm();
                  setOpenCreateQuestionDialog(true);
                }}
                disabled={!promotion}
                size="small"
              >
                Add Question
              </MDButton>
            </MDBox>

            {pageLoaded && (
              <DataTable
                table={{ columns: questionColumns, rows: questions }}
                canSearch
                showTotalEntries
                entriesPerPage={false}
                isSorted={false}
              />
            )}

            {pageLoaded && questions.length === 0 && (
              <MDTypography variant="button" color="text">
                No questions found for this promotion.
              </MDTypography>
            )}
          </MDBox>
        </Card>
      </MDBox>

      <Dialog
        open={openCreateQuestionDialog}
        onClose={() => setOpenCreateQuestionDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add Question</DialogTitle>

        <DialogContent>
          <MDBox display="flex" flexDirection="column" gap={3} pt={1}>
            <MDTypography variant="body2" fontWeight="regular">
              Question Configuration
            </MDTypography>

            <MDInput
              label="Question"
              value={newQuestion}
              onChange={(event) => setNewQuestion(event.target.value)}
              fullWidth
              multiline
              rows={3}
            />

            <MDInput
              select
              label="Question Type"
              value={newQuestionType}
              onChange={(event) => setNewQuestionType(event.target.value)}
              fullWidth
              SelectProps={{ native: true }}
            >
              <option value={null}>Select Type</option>
              <option value="DIRECT_ANSWERS">Direct Answers</option>
              <option value="OPTIONS_ANSWERS">Question Options</option>
            </MDInput>

            {newQuestionType === "OPTIONS_ANSWERS" && (
              <MDBox display="flex" flexDirection="column" gap={2}>
                <MDBox display="flex" justifyContent="space-between" alignItems="center" gap={2}>
                  <MDBox>
                    <MDTypography variant="subtitle2">Question Options</MDTypography>
                    <MDTypography variant="body2" color="text" fontSize={12}>
                      Add the possible options participants will predict.
                    </MDTypography>
                  </MDBox>

                  <MDButton
                    size="small"
                    variant="outlined"
                    color="dark"
                    startIcon={<Icon>add</Icon>}
                    onClick={addQuestionOption}
                  >
                    Add
                  </MDButton>
                </MDBox>

                {newQuestionOptions.map((option, index) => (
                  <MDBox
                    key={`question-option-${index}`}
                    display="flex"
                    alignItems="center"
                    gap={1}
                  >
                    <MDBox flex={1}>
                      <MDInput
                        label={`Option ${index + 1}`}
                        value={option}
                        onChange={(event) => updateQuestionOption(index, event.target.value)}
                        fullWidth
                      />
                    </MDBox>

                    <Tooltip title="Remove Option">
                      <span>
                        <MDButton
                          size="small"
                          variant="outlined"
                          color="error"
                          iconOnly
                          onClick={() => removeQuestionOption(index)}
                          disabled={newQuestionOptions.length <= 2}
                        >
                          <Icon>delete</Icon>
                        </MDButton>
                      </span>
                    </Tooltip>
                  </MDBox>
                ))}
              </MDBox>
            )}

            <Divider />

            <MDTypography variant="body2" fontWeight="regular">
              Answer Configuration
            </MDTypography>

            <MDInput
              select
              label="Answer Type"
              value={newAnswerType}
              onChange={(event) => setNewAnswerType(event.target.value)}
              fullWidth
              SelectProps={{ native: true }}
            >
              <option value={null}>Select Type</option>
              <option value="CONTROLLED">Controlled</option>
              <option value="OPEN">Open</option>
            </MDInput>

            {newAnswerType === "OPEN" && (
              <MDInput
                select
                label="Answer Value Type"
                value={newAnswerValueType}
                onChange={(event) => setNewAnswerValueType(event.target.value)}
                fullWidth
                SelectProps={{ native: true }}
              >
                <option value={null}>Select Type</option>
                <option value="TEXT">Text</option>
                <option value="INTEGER">Integer</option>
                <option value="DECIMAL">Decimal</option>
                <option value="DATE">Date</option>
                <option value="DATETIME">Date and Time</option>
                <option value="BOOLEAN">Boolean</option>
              </MDInput>
            )}

            {newAnswerType === "CONTROLLED" && (
              <MDBox display="flex" flexDirection="column" gap={2}>
                <MDInput
                  select
                  label="Selection Mode"
                  value={newSelectionCardinality}
                  onChange={(event) => setNewSelectionCardinality(event.target.value)}
                  fullWidth
                  SelectProps={{ native: true }}
                >
                  <option value={null}>Select Mode</option>
                  <option value="SINGLE">Single</option>
                  <option value="MULTIPLE">Multiple</option>
                </MDInput>

                <MDBox mt={1} display="flex" justifyContent="space-between" alignItems="center" gap={2}>
                  <MDBox>
                    <MDTypography variant="subtitle2">Controlled Answer Choices</MDTypography>
                    <MDTypography variant="body2" color="text" fontSize={12}>
                      These choices populate the answer select field shown to participants.
                    </MDTypography>
                  </MDBox>

                  <MDButton
                    size="small"
                    variant="outlined"
                    color="dark"
                    startIcon={<Icon>add</Icon>}
                    onClick={addControlledAnswer}
                  >
                    Add
                  </MDButton>
                </MDBox>

                {newControlledAnswers.map((answer, index) => (
                  <MDBox
                    key={`controlled-answer-${index}`}
                    display="flex"
                    flexDirection={{ xs: "column", sm: "row" }}
                    alignItems={{ xs: "stretch", sm: "center" }}
                    gap={1}
                  >
                    <MDBox flex={1}>
                      <MDInput
                        label={`Answer ${index + 1} Label`}
                        value={answer.label}
                        onChange={(event) =>
                          updateControlledAnswer(index, "label", event.target.value)
                        }
                        fullWidth
                      />
                    </MDBox>

                    <MDBox flex={1}>
                      <MDInput
                        label="Value"
                        value={answer.value}
                        onChange={(event) =>
                          updateControlledAnswer(index, "value", event.target.value)
                        }
                        fullWidth
                      />
                    </MDBox>

                    <Tooltip title="Remove Answer">
                      <span>
                        <MDButton
                          size="small"
                          variant="outlined"
                          color="error"
                          iconOnly
                          onClick={() => removeControlledAnswer(index)}
                          disabled={newControlledAnswers.length <= 2}
                        >
                          <Icon>delete</Icon>
                        </MDButton>
                      </span>
                    </Tooltip>
                  </MDBox>
                ))}
              </MDBox>
            )}

            <Divider />

            <MDTypography variant="body2" fontWeight="regular">
              Prediction Window
            </MDTypography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <MDInput
                  label="Opens At"
                  type="datetime-local"
                  value={newQuestionOpensAt}
                  onChange={(event) => setNewQuestionOpensAt(event.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <MDInput
                  label="Closes At"
                  type="datetime-local"
                  value={newQuestionClosesAt}
                  onChange={(event) => setNewQuestionClosesAt(event.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </MDBox>
        </DialogContent>

        <DialogActions>
          <MDButton
            variant="outlined"
            color="dark"
            onClick={() => setOpenCreateQuestionDialog(false)}
          >
            Cancel
          </MDButton>

          <MDButton variant="contained" color="dark" onClick={createQuestion}>
            Create Question
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Edit Promotion</DialogTitle>

        <DialogContent>
          <MDBox display="flex" flexDirection="column" gap={2} pt={1}>
            <MDInput
              label="Promotion Title"
              value={promotionTitle}
              onChange={(event) => setPromotionTitle(event.target.value)}
              fullWidth
            />

            <MDInput
              label="Description"
              value={promotionDescription}
              onChange={(event) => setPromotionDescription(event.target.value)}
              fullWidth
              multiline
              rows={3}
            />

            <MDInput
              label="Opens At"
              type="datetime-local"
              value={promotionOpensAt}
              onChange={(event) => setPromotionOpensAt(event.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />

            <MDInput
              label="Closes At"
              type="datetime-local"
              value={promotionClosesAt}
              onChange={(event) => setPromotionClosesAt(event.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </MDBox>
        </DialogContent>

        <DialogActions>
          <MDButton variant="outlined" color="dark" onClick={() => setOpenEditDialog(false)}>
            Cancel
          </MDButton>

          <MDButton variant="contained" color="dark" onClick={editPromotion}>
            Save Changes
          </MDButton>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Footer />
    </DashboardLayout>
  );
}

export default PredictPromotionPage;
